import React, { Component } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Grid, Header, Icon, Table } from 'semantic-ui-react';
import styled from 'styled-components';
import { Privilege } from 'ts-types/api.enums';

const StyledIcon = styled(Icon)`
    cursor: pointer;
`;

const StyledBtnContainer = styled.div`
  margin-bottom: 5px;
  white-space: nowrap !important;
`;

const StyledButtons = styled(Grid.Column)`
  padding: 0 !important;
  margin: 0 0 0 4px !important;
  flex: 1 1 auto;
`;

const StyledTableRight = styled(Grid.Column)`
    padding-right: 1rem !important;
    padding-left: 0 !important;
    flex: 50 50 auto;
`;

const StyledTableLeft = styled(Grid.Column)`
    padding-left: 1rem !important;
    padding-right: 0 !important;
    flex: 50 50 auto;
`;

const StyledTableRow = styled(Table.Row)`
  cursor: pointer;
`;

const ScrollableTableWrapper = styled.div`
  height: 375px;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  border: 1px solid rgba(34,36,38,.15);
  box-shadow: none;
  border-radius: .28571429rem;
  flex: 50 50 auto;

  & .ui.table {
    border: none;
  }

   & .ui.table thead tr:first-child > th {
      position: sticky !important;
      top: 0;
      z-index: 2;
      background: #fff;
  }

  & .ui.table tbody tr:last-child > td {
      border-bottom: 1px solid rgba(34,36,38,.15)
  }
`;

interface Props extends MessageSourceProps {
  privileges?: Array<Privilege>,
  selectedPrivileges?: Array<Privilege>,
  availablePrivileges?: Array<Privilege>,
  setSelectedPrivileges: (selectedPrivileges: Array<Privilege>) => void
}

interface State {
  selectedAddPrivileges?: Array<Privilege>,
  availablePrivileges?: Array<Privilege>,
  markedPrivileges: Array<Privilege>
}

class RolePrivilegesSegment extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {selectedPrivileges = [], availablePrivileges = []} = this.props;

    this.state = {
      selectedAddPrivileges: selectedPrivileges,
      availablePrivileges: availablePrivileges,
      markedPrivileges: []
    };
  }

  privilegeSelection = (privilege: Privilege) => {

    const {markedPrivileges, availablePrivileges = [], selectedAddPrivileges = []} = this.state;

    if (markedPrivileges.length === 0) {
      this.setState({
        markedPrivileges: [privilege]
      })
    } else if (markedPrivileges.includes(privilege)) {
      const filter = markedPrivileges.filter(priv => priv !== privilege);

      this.setState({
        markedPrivileges: [...filter]
      })
    } else if (availablePrivileges.includes(privilege)) {
      const filter = markedPrivileges.filter(priv => selectedAddPrivileges.includes(priv));

      if (filter.length > 0) {
        this.setState({
          markedPrivileges: [privilege]
        })
      } else {
        this.setState({
          markedPrivileges: [...markedPrivileges, privilege]
        })
      }
    } else {
      const filter = markedPrivileges.filter(priv => availablePrivileges.includes(priv));

      if (filter.length > 0) {
        this.setState({
          markedPrivileges: [privilege]
        })
      } else {
        this.setState({
          markedPrivileges: [...markedPrivileges, privilege]
        })
      }
    }
  };

  addRemovePrivilege = () => {

    const {setSelectedPrivileges} = this.props;
    const {markedPrivileges = [], selectedAddPrivileges = [], availablePrivileges = []} = this.state;

    if (markedPrivileges.length > 0) {
      if (selectedAddPrivileges.some(priv => markedPrivileges.includes(priv))) {

        const filteredSelectedPrivileges = selectedAddPrivileges.filter(priv => !markedPrivileges.includes(priv));

        this.setState({
          availablePrivileges: [
            ...availablePrivileges,
            ...markedPrivileges
          ],
          selectedAddPrivileges: filteredSelectedPrivileges,
          markedPrivileges: []
        });

        setSelectedPrivileges(filteredSelectedPrivileges);
      } else {

        const filteredPrivileges = availablePrivileges.filter(priv => !markedPrivileges.includes(priv));
        const selectedPrivileges = [
          ...selectedAddPrivileges,
          ...markedPrivileges
        ];

        this.setState({
          selectedAddPrivileges: selectedPrivileges,
          availablePrivileges: filteredPrivileges,
          markedPrivileges: []
        });

        setSelectedPrivileges(selectedPrivileges);
      }
    }
  };

  markUnmarkAllPrivileges = (markPrivileges: Array<Privilege>) => {
    this.setState({
      markedPrivileges: [...markPrivileges]
    })
  };

  render(): React.ReactNode {

    const {selectedAddPrivileges = [], availablePrivileges = [], markedPrivileges = []} = this.state;
    const {privileges = [], getMessage} = this.props;

    const selectablePrivileges: Array<JSX.Element> = [];
    const usedPrivileges: Array<JSX.Element> = [];

    privileges.forEach(priv => {

      const active = markedPrivileges.includes(priv);

      if (priv && availablePrivileges.includes(priv)) {

        const availablePrivilegeDescription = (
            <StyledTableRow key={priv}
                            onClick={() => this.privilegeSelection(priv)}
                            active={active}
            >
              <Table.Cell>{priv}</Table.Cell>
            </StyledTableRow>
        );

        selectablePrivileges.push(availablePrivilegeDescription)
      }

      if (priv && selectedAddPrivileges.includes(priv)) {

        const usedPrivilegeDescription = (
            <StyledTableRow key={priv}
                            onClick={() => this.privilegeSelection(priv)}
                            active={active}
            >
              <Table.Cell>{priv}</Table.Cell>
            </StyledTableRow>
        );

        usedPrivileges.push(usedPrivilegeDescription);
      }
    });

    const disabledLeft = markedPrivileges.length === 0 ||
        availablePrivileges.some(priv => markedPrivileges.includes(priv));
    const disabledRight = markedPrivileges.length === 0 ||
        selectedAddPrivileges.some(priv => markedPrivileges.includes(priv));

    return (
        <Grid>
          <Grid.Row>
            <Grid.Column>
              <Grid>
                <Grid.Row>
                  <StyledTableLeft>
                    {this.renderLeftTableSegment(getMessage("available"), selectablePrivileges)}
                  </StyledTableLeft>
                  <StyledButtons textAlign='center' verticalAlign='middle'>
                    <StyledBtnContainer>
                      <StyledIcon bordered
                                  onClick={() => this.markUnmarkAllPrivileges(availablePrivileges)}
                                  name='clipboard list'
                      />
                      <StyledIcon bordered
                                  onClick={() => this.markUnmarkAllPrivileges(selectedAddPrivileges)}
                                  name='clipboard list'
                      />
                    </StyledBtnContainer>
                    <StyledBtnContainer>
                      <StyledIcon bordered
                                  onClick={() => this.markUnmarkAllPrivileges([])}
                                  name='clipboard outline'
                      />
                      <StyledIcon bordered
                                  onClick={() => this.markUnmarkAllPrivileges([])}
                                  name='clipboard outline'
                      />
                    </StyledBtnContainer>
                    <StyledBtnContainer>
                      <StyledIcon bordered
                                  disabled={disabledRight}
                                  onClick={this.addRemovePrivilege}
                                  name='angle double right'
                      />
                      <StyledIcon bordered
                                  disabled={disabledLeft}
                                  onClick={this.addRemovePrivilege}
                                  name='angle double left'
                      />
                    </StyledBtnContainer>
                  </StyledButtons>
                  <StyledTableRight>
                    {this.renderRightTableSegment(getMessage("selected"), usedPrivileges)}
                  </StyledTableRight>
                </Grid.Row>
              </Grid>
            </Grid.Column>
          </Grid.Row>
        </Grid>
    );
  }

  renderRightTableSegment = (headerText: string, usedPrivileges: Array<React.ReactNode>): React.ReactNode => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <Header as='h5'>
            {headerText}:
          </Header>
          <ScrollableTableWrapper>
            <Table celled selectable size='small'>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{getMessage("description")}</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {usedPrivileges}
              </Table.Body>
            </Table>
          </ScrollableTableWrapper>
        </React.Fragment>
    )
  };

  renderLeftTableSegment = (headerText: string, usedPrivileges: Array<React.ReactNode>): React.ReactNode => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <Header as='h5'>
            {headerText}:
          </Header>
          <ScrollableTableWrapper>
            <Table celled selectable size='small'>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{getMessage("description")}</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {usedPrivileges}
              </Table.Body>
            </Table>
          </ScrollableTableWrapper>
        </React.Fragment>
    )
  };

}

export default withMessages(RolePrivilegesSegment);
